import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { LayoutTemplate, ShieldCheck, Target } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { Select } from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import {
  marketingApi,
  type MarketingSequenceIntakeAnswer,
  type MarketingSequenceIntakeMode,
  type MarketingSequenceIntakeQuestion,
  type MarketingSequenceIntakeSummary,
} from '../../../lib/api';

interface MarketingSequenceIntakeModalProps {
  isOpen: boolean;
  marketingBrandId: string;
  intake?: MarketingSequenceIntakeSummary | null;
  onClose: () => void;
  onOpenBlankBuilder: () => void;
}

function errorMessage(error: unknown): string {
  if (isAxiosError<{ error?: string }>(error)) {
    return error.response?.data?.error || 'Nie udało się zapisać briefu. Sprawdź pola i spróbuj ponownie.';
  }
  return 'Nie udało się zapisać briefu. Spróbuj ponownie.';
}

function QuestionField({
  question,
  value,
  onChange,
}: {
  question: MarketingSequenceIntakeQuestion;
  value: MarketingSequenceIntakeAnswer | undefined;
  onChange: (value: MarketingSequenceIntakeAnswer | undefined) => void;
}) {
  const id = `marketing-intake-${question.key}`;
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-text-primary">
        {question.label}{question.required ? <span className="ml-1 text-danger">*</span> : null}
      </label>
      <p className="text-xs leading-relaxed text-text-muted">{question.description}</p>
      {question.type === 'LONG_TEXT' ? (
        <Textarea
          id={id}
          value={typeof value === 'string' ? value : ''}
          placeholder={question.placeholder}
          maxLength={question.maxLength}
          autoResize
          minRows={3}
          onChange={event => onChange(event.target.value)}
        />
      ) : question.type === 'SINGLE_SELECT' ? (
        <Select
          id={id}
          value={typeof value === 'string' ? value : ''}
          placeholder="Wybierz"
          onChange={event => onChange(event.target.value || undefined)}
        >
          <option value="">Wybierz</option>
          {question.options?.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Select>
      ) : (
        <Input
          id={id}
          type={question.type === 'NUMBER' ? 'number' : 'text'}
          value={value ?? ''}
          placeholder={question.placeholder}
          maxLength={question.maxLength}
          min={question.min}
          max={question.max}
          onChange={event => {
            if (question.type !== 'NUMBER') {
              onChange(event.target.value);
              return;
            }
            onChange(event.target.value === '' ? undefined : Number(event.target.value));
          }}
        />
      )}
    </div>
  );
}

export function MarketingSequenceIntakeModal({
  isOpen,
  marketingBrandId,
  intake,
  onClose,
  onOpenBlankBuilder,
}: MarketingSequenceIntakeModalProps) {
  const queryClient = useQueryClient();
  const latestVersion = intake?.versions[0];
  const [mode, setMode] = useState<MarketingSequenceIntakeMode | null>(null);
  const [selectionKey, setSelectionKey] = useState('');
  const [labProductId, setLabProductId] = useState('');
  const [answers, setAnswers] = useState<Record<string, MarketingSequenceIntakeAnswer>>({});

  useEffect(() => {
    if (!isOpen) return;
    setMode(latestVersion?.mode ?? null);
    setSelectionKey(latestVersion?.selectionKey ?? '');
    setLabProductId(latestVersion?.labProductId ?? '');
    setAnswers(latestVersion?.brief.answers ?? {});
  }, [isOpen, latestVersion]);

  const catalogQuery = useQuery({
    queryKey: ['marketing-sequence-intake-catalog', marketingBrandId],
    queryFn: async () => (await marketingApi.getSequenceIntakeCatalog(marketingBrandId)).data,
    enabled: isOpen && Boolean(marketingBrandId),
    staleTime: 60_000,
  });
  const modeDefinition = catalogQuery.data?.modes.find(item => item.mode === mode);
  const choice = modeDefinition?.choices.find(item => item.key === selectionKey);
  const missingRequired = useMemo(() => choice?.questions.some(question => {
    if (!question.required) return false;
    const value = answers[question.key];
    return value === undefined || value === '' || (typeof value === 'string' && !value.trim());
  }) ?? true, [answers, choice]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!mode || !selectionKey) throw new Error('Wybierz sposób i kierunek sekwencji');
      const payload = { mode, selectionKey, labProductId: labProductId || null, answers };
      if (intake) {
        return (await marketingApi.createSequenceIntakeVersion(marketingBrandId, intake.id, {
          ...payload,
          expectedLatestVersionNumber: intake.latestVersionNumber,
        })).data;
      }
      return (await marketingApi.createSequenceIntake(marketingBrandId, payload)).data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['marketing-sequence-intakes', marketingBrandId] });
      onClose();
    },
  });

  const changeMode = (nextMode: MarketingSequenceIntakeMode) => {
    if (intake) return;
    setMode(nextMode);
    setSelectionKey('');
    setAnswers({});
    saveMutation.reset();
  };

  const footer = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <Button
        variant="primary"
        loading={saveMutation.isPending}
        disabled={!mode || !selectionKey || missingRequired || catalogQuery.isLoading}
        onClick={() => saveMutation.mutate()}
      >
        {intake ? 'Zapisz nową wersję briefu' : 'Zapisz brief'}
      </Button>
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        {!intake ? <Button variant="ghost" onClick={onOpenBlankBuilder}>Otwórz pusty builder</Button> : null}
        <Button variant="secondary" onClick={onClose}>Anuluj</Button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={intake ? `Edytuj brief: ${intake.name}` : 'Nowa automatyzacja'}
      description="Zapisz cel i kontekst. Generowanie grafu zostanie podłączone w następnym, osobno walidowanym etapie."
      footer={footer}
      size="2xl"
    >
      {catalogQuery.isLoading ? (
        <div className="py-12 text-center text-sm text-text-muted">Ładowanie kontraktu briefu...</div>
      ) : catalogQuery.isError ? (
        <div className="rounded-card border border-danger/30 bg-danger/5 p-4 text-sm text-danger">
          Nie udało się pobrać definicji briefu. Zamknij okno i spróbuj ponownie.
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid gap-3 md:grid-cols-2">
            {catalogQuery.data?.modes.map(item => {
              const selected = mode === item.mode;
              const Icon = item.mode === 'GOAL' ? Target : LayoutTemplate;
              return (
                <button
                  key={item.mode}
                  type="button"
                  disabled={Boolean(intake)}
                  aria-pressed={selected}
                  onClick={() => changeMode(item.mode)}
                  className={`rounded-card border p-4 text-left transition-colors ${selected ? 'border-accent-primary/60 bg-accent-primary/10' : 'border-border-subtle bg-surface-base/40 hover:border-accent-primary/30'} disabled:cursor-default disabled:opacity-80`}
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                    <Icon className="h-4 w-4 text-accent-primary" aria-hidden="true" />
                    {item.label}
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-text-muted">{item.description}</p>
                </button>
              );
            })}
          </div>

          {modeDefinition ? (
            <div className="space-y-1.5">
              <label htmlFor="marketing-intake-selection" className="block text-sm font-medium text-text-primary">
                {mode === 'GOAL' ? 'Cel biznesowy' : 'Blueprint'}
              </label>
              <Select
                id="marketing-intake-selection"
                value={selectionKey}
                disabled={Boolean(intake)}
                placeholder="Wybierz kierunek"
                onChange={event => {
                  setSelectionKey(event.target.value);
                  setAnswers({});
                  saveMutation.reset();
                }}
              >
                <option value="">Wybierz kierunek</option>
                {modeDefinition.choices.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
              </Select>
              {choice ? <p className="text-xs text-text-muted">{choice.description}</p> : null}
            </div>
          ) : null}

          {choice ? (
            <>
              <div className="space-y-4 border-t border-border-subtle pt-5">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">Dynamiczny brief</h3>
                  <p className="mt-1 text-xs text-text-muted">Pola poniżej wynikają z wybranego celu lub blueprintu.</p>
                </div>
                <div className="grid gap-5 md:grid-cols-2">
                  {choice.questions.map(question => (
                    <QuestionField
                      key={question.key}
                      question={question}
                      value={answers[question.key]}
                      onChange={value => setAnswers(current => {
                        const next = { ...current };
                        if (value === undefined || value === '') delete next[question.key];
                        else next[question.key] = value;
                        return next;
                      })}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-1.5 border-t border-border-subtle pt-5">
                <label htmlFor="marketing-intake-product" className="block text-sm font-medium text-text-primary">Źródło wiedzy o produkcie</label>
                <p className="text-xs leading-relaxed text-text-muted">
                  Opcjonalnie wskaż produkt Laboratorium połączony ze sklepem tej marki. Nazwy produktów i marek nie są używane do łączenia danych.
                </p>
                <Select id="marketing-intake-product" value={labProductId} onChange={event => setLabProductId(event.target.value)}>
                  <option value="">Bez produktu Laboratorium</option>
                  {catalogQuery.data?.eligibleLabProducts.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name}{product.hasCompleteKnowledgeBase ? ' - baza wiedzy gotowa' : ' - baza wiedzy niepełna'}
                    </option>
                  ))}
                </Select>
              </div>
            </>
          ) : null}

          <div className="flex gap-3 rounded-card border border-accent-primary/20 bg-accent-primary/5 p-4">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-accent-primary" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-text-primary">Zapis pozostaje fail-closed</p>
              <p className="mt-1 text-xs leading-relaxed text-text-muted">
                Ten krok nie uruchamia AI, joba, publikacji, providera ani wysyłki. Tworzy wyłącznie wersjonowany brief wejściowy dla M3-02.
              </p>
            </div>
          </div>

          {saveMutation.isError ? (
            <div role="alert" className="rounded-card border border-danger/30 bg-danger/5 p-3 text-sm text-danger">
              {errorMessage(saveMutation.error)}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
